<template>
  <div class="min-h-screen flex place-items-center justify-center" v-if="!ready">
    <IconSpinner class="animate-spin w-12 h-12" />
  </div>
  <template v-else>
    <nav class="w-48 h-full fixed p-4 overflow-y-auto bg-secondary">
      <img alt="AirSage logo" class="h-10 mb-6 mx-auto" src="@/assets/img/logo_long_white.png" />
      <div class="flex flex-col justify-between space-y-2 divide-y divide-zinc-500">
        <ul class="space-y-1 font-medium tracking-wide">
          <li>
            <NavMenuItem to="organizations">
              <template #icon>
                <IconOrganization class="w-6 h-6" />
              </template>
            </NavMenuItem>
          </li>
          <li>
            <NavMenuItem to="users">
              <template #icon>
                <IconAccounts class="w-6 h-6" />
              </template>
            </NavMenuItem>
          </li>
          <li>
            <NavMenuItem to="modules">
              <template #icon>
                <IconModules class="w-6 h-6" />
              </template>
            </NavMenuItem>
          </li>
        </ul>
        <div class="pt-2">
          <div
            class="flex items-center p-2 text-zinc-400 rounded-lg hover:bg-zinc-50/5 hover:text-zinc-50 cursor-pointer"
            @click="logoutUser">
            <IconLogout class="size-6" />
            <span class="font-medium ml-3 capitalize">Logout</span>
          </div>
        </div>
      </div>
    </nav>
    <main class="ml-48">
      <RouterView />
      <ToastAlert />
    </main>
  </template>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { RouterView } from "vue-router";
import { useAuth0 } from "@auth0/auth0-vue";
import { getCurrentScope } from "@sentry/vue";
import { get, nth } from "lodash";
import NavMenuItem from "@/components/NavMenuItem.vue";
import ToastAlert from "@/components/ToastAlert.vue";
import IconSpinner from "@/components/icons/IconSpinner.vue";
import IconOrganization from "@/components/icons/IconOrganization.vue";
import IconLogout from "@/components/icons/IconLogout.vue";
import IconAccounts from "@/components/icons/IconAccounts.vue";
import IconModules from "@/components/icons/IconModules.vue";
import {
  UsersService,
  OrganizationsService,
  ModulesService
} from "@/services";
import { useUserStore } from "@/stores/user";
import { useMainStore } from "@/stores/main";

const userStore = useUserStore();
const mainStore = useMainStore();
const {
  isLoading,
  isAuthenticated,
  user,
  getAccessTokenSilently,
  logout
} = useAuth0();

const ready = computed(() => !isLoading.value && isAuthenticated.value && userLoaded.value);
const userLoaded = ref(false);

const loadUser = async () => {
  try {
    const id = nth(get(user, "value.sub", "|").split('|'), 1);
    const token = await getAccessTokenSilently();

    const userData = await UsersService.read(id, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const scope = getCurrentScope();
    const { email, name } = user.value;
    scope.setUser({ id, email, username: name });

    userStore.user = { ...userData, ...user.value };
    if (userStore.isAdmin) {
      userStore.organizations = await OrganizationsService.list({
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    } else {
      logout();
    }
  } catch (error) {
    console.error(error);
  }
};

const fetchOrganizations = async() => {
  try {
    const token = await getAccessTokenSilently();
    mainStore.organizations = await OrganizationsService.list({
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error(error);
  }
};

const fetchUsers = async () => {
  try {
    const token = await getAccessTokenSilently();
    mainStore.users = await UsersService.list({
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error(error);
  }
};

const fetchModules = async () => {
  try {
    const token = await getAccessTokenSilently();
    mainStore.modules = await ModulesService.list({
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error(error);
  }
};

watch(isLoading, async () => {
  if (!isLoading.value && isAuthenticated.value) {
    await loadUser();
    await fetchUsers();
    await fetchModules();
    await fetchOrganizations();
    userLoaded.value = true;
  }
}, {
  immediate: true
});

const logoutUser = () => {
  logout({
    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
    returnTo: `${window.location.origin}/logout`
  });
};
</script>

<style>
@import "@/assets/style/main.css";
</style>
